<template>
  <div>
    <NuxtLayout
      name="asuostaticlayout"
      :title="$page?.studentsServices?.title"
      :image-source="$page?.studentsServices?.heroImage"
      :is-sticky-brand-nav="false"
    >
      <NavbarStickyAtlas :nav-items="navItems" title="On this page:">
        <template #stuckMenu>
          <div class="d-flex justify-content-end flex-direction-row mb-space-xxxs">
            <ButtonApplyNow
              :type="applyNowType"
              :link-url="applyNowUrl"
              event-region="navbar"
              event-section="sticky navbar"
            ></ButtonApplyNow>
            <button
              class="btn btn-secondary fs-medium fw-bold ms-space-sm"
              @click="
                scrollToRfi(
                  'sticky navbar',
                  'main content',
                  'navbar-sticky-atlas',
                  'asuo-static-hero'
                )
              "
            >
              Request info
            </button>
          </div>
        </template>
      </NavbarStickyAtlas>

      <!-- section-introduction -->
      <SectionOverlapApollo
        id="services-support-team"
        :image-source="$page?.studentsServices?.introImage!"
        image-position="right"
        :title="$page?.studentsServices?.introTitle!"
        title-level="h2"
        title-size="medium"
        :display-cta="false"
        section-bg-color="green"
        class="py-lg-space-xxl intro-section"
        :style="getIntroBgPattern"
        :image-alt="$page?.studentsServices?.introImageAlt!"
      >
        <template #body>
          <div class="mb-space-md mb-lg-space-lg">
            <div
              class="mb-space-sm mb-lg-space-md text-white intro-text-area"
              v-html="$page?.studentsServices?.introDescription"
            ></div>
            <NuxtLink
              v-if="$page?.studentsServices?.introLink"
              href="#"
              class="btn btn-secondary px-space-md supportTeam"
              @click="
                handleSuccessTeamLinkClick(
                  $page?.studentsServices?.introLink! as unknown as ParagraphHtmlLink,
                  $page?.studentsServices?.introTitle!
                )
              "
              >{{ $page?.studentsServices?.introLink?.text }}
            </NuxtLink>
          </div>
        </template>
      </SectionOverlapApollo>

      <!-- accordion section -->
      <section
        id="services-student-services"
        class="service-accordion-section py-0 py-md-space-md py-lg-space-lg"
      >
        <div class="container px-space-xs px-lg-space-lg py-space-lg py-lg-space-lg">
          <div class="row gx-0 flex-column">
            <h2
              class="mb-space-sm px-0 col-12 col-lg-8 w-auto h2-medium text-dark-3 align-self-start"
            >
              {{ $page?.studentsServices?.accordionTitle }}
            </h2>
            <div class="accordion-highlight bg-secondary mb-space-md"></div>
          </div>
          <OverlapAccordionAtlas
            default-accordion-image="getDefaultAccordionImage"
            :accordion-items="$page?.studentsServices?.accordionItems! as unknown as Item[]"
            @collapse-shown="
              handleCollapseEvent('open', $page?.studentsServices?.accordionTitle!, $event.title!)
            "
            @collapse-hidden="
              handleCollapseEvent('close', $page?.studentsServices?.accordionTitle!, $event.title!)
            "
          />
        </div>
      </section>

      <!-- career section -->
      <SectionServicesGrid
        id="services-career-services"
        :title="$page?.studentsServices?.careerTitle!"
        :subtitle="$page?.studentsServices?.careerSubtitle!"
        :description="$page?.studentsServices?.careerDescription!"
        :link="$page?.studentsServices?.careerLink! as unknown as ParagraphHtmlLink"
        slot-bg-color="dark-3"
        slot-bg-variant="dark-3"
      >
        <template #content>
          <div class="py-space-lg px-space-md py-lg-space-xxl px-lg-space-lg">
            <div class="career-grid">
              <div
                v-for="(item, index) in $page?.studentsServices?.careerListItem"
                :key="index"
                class="text-white icon-list"
              >
                <div class="bg-secondary career-icon">
                  <img :src="item?.icon!" :alt="item?.iconAlt!" />
                </div>
                <p class="mb-0">{{ item?.title }}</p>
              </div>
            </div>
          </div>
        </template>
      </SectionServicesGrid>

      <!-- community section -->
      <SectionServicesGrid
        id="services-community"
        :title="$page?.studentsServices?.communitiesTitle!"
        :subtitle="$page?.studentsServices?.communitiesSubtitle!"
        :description="$page?.studentsServices?.communitiesDescription!"
        :link="$page?.studentsServices?.communitiesLink! as unknown as ParagraphHtmlLink"
        slot-position="left"
        :section-padding-top="false"
        :display-bg-pattern="true"
        :bg-pattern-src="$page?.studentsServices?.testimonialBackground!"
      >
        <template #content>
          <div
            class="community-img h-100 rds-lazy"
            :data-background-image="$page?.studentsServices?.communitiesImage"
          ></div>
        </template>
      </SectionServicesGrid>

      <!-- support section -->
      <SectionServicesSupportGrid
        id="services-support"
        :title="$page?.studentsServices?.supportTitle!"
        :description="$page?.studentsServices?.supportDescription!"
        :link="$page?.studentsServices?.supportLink! as unknown as ParagraphHtmlLink"
        slot-position="left"
        :section-padding-top="false"
        :display-bg-pattern="true"
        :bg-pattern-src="$page?.studentsServices?.testimonialBackground!"
      >
        <template #content>
          <div
            class="support-img h-100 rds-lazy"
            :data-background-image="$page?.studentsServices?.supportImage"
          ></div>
        </template>
      </SectionServicesSupportGrid>

      <!-- testimonial section -->
      <CarouselCardApollo
        v-if="$page?.studentsServices?.testimonialCards"
        :title="$page?.studentsServices?.testimonialTitle!"
        :slides="$page?.studentsServices?.testimonialCards"
        section-background-variant="light-1"
        @next-slide-click="
          handleCarouselClick(
            $page?.studentsServices?.testimonialTitle!,
            'carousel',
            'right chevron'
          )
        "
        @previous-slide-click="
          handleCarouselClick(
            $page?.studentsServices?.testimonialTitle!,
            'carousel',
            'left chevron'
          )
        "
        @indicator-click="
          handleCarouselClick($page?.studentsServices?.testimonialTitle!, 'pagination', 'owl dot')
        "
      >
        <template #above-title>
          <p class="d-inline-block fw-bold px-space-xxs py-space-xxxs mb-space-xxs bg-secondary">
            Still curious?
          </p>
        </template>
        <template #default="slotProps">
          <div v-for="(item, index) in slotProps.paginatedSlides" :key="index" class="col">
            <div
              class="bg-dark-3 py-space-lg d-flex flex-column justify-content-center align-items-center text-light-1 h-100"
            >
              <img
                v-if="item!.authorImage"
                :src="item!.authorImage"
                alt="authorImage"
                class="mb-space-md carousel-img"
              />
              <h2 class="h2-small mb-space-xxs">{{ item!.authorName }}</h2>
              <p class="fs-small mb-space-sm">{{ item!.authorDesignation }}</p>
              <button
                class="text-secondary btn btn-link text-decoration-underline supportTeam"
                @click="showQuoteModal(item as unknown as ParagraphTestimonialApollo)"
              >
                See full quote
              </button>
            </div>
          </div>
        </template>
      </CarouselCardApollo>
      <RdsModal
        ref="quoteModal"
        use-custom-close
        :visible="displayModal"
        centered
        @hidden="displayModal = false"
      >
        <div class="p-space-md py-space-lg">
          <div class="d-flex justify-content-end align-items-center">
            <button
              type="button"
              aria-label="Close"
              class="modal-close ms-auto"
              @click="handleModalClose()"
            >
              ×
            </button>
          </div>
          <div class="px-space-md py-space-lg" v-html="modalContent"></div>
        </div>
      </RdsModal>
      <!-- section faq -->
      <section id="services-faq" class="py-space-lg py-lg-space-xxl bg-dark-3">
        <div class="container">
          <div class="row gx-0">
            <div class="col-12 bg-light-2 px-space-sm py-space-lg px-lg-space-xl py-lg-space-xxl">
              <h2 class="h2-medium mb-space-sm mb-lg-space-md">
                {{ $page?.studentsServices?.faqTitle }}
              </h2>
              <div class="bg-secondary faq-highlight mb-space-md"></div>
              <div
                v-for="(item, index) in $page?.studentsServices?.faqItems"
                :key="index"
                class="accordion-gap"
              >
                <collapse-item
                  :display-number="false"
                  :collapse-id="`faq-collapse-${index}`"
                  :title="item?.title!"
                  :highlight-width="6"
                  class="bg-white"
                  @collapse-shown="
                    handleFaqCollapseClick('open', $page?.studentsServices?.faqTitle!, item?.title!)
                  "
                  @collapse-hidden="
                    handleFaqCollapseClick(
                      'close',
                      $page?.studentsServices?.faqTitle!,
                      item?.title!
                    )
                  "
                >
                  <div v-html="item?.description"></div>
                </collapse-item>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- section-explore-your-future -->
      <SectionExploreYourFuture
        :title="$page?.studentsServices?.exploreTitle!"
        :background="$page?.studentsServices?.exploreBg!"
        :items="$page?.studentsServices?.exploreCards! as ExploreItem[]"
      ></SectionExploreYourFuture>
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { CarouselCardApollo } from "@rds-vue-ui/carousel-card-apollo";
import type { Item } from "@rds-vue-ui/overlap-accordion-atlas";
import { OverlapAccordionAtlas } from "@rds-vue-ui/overlap-accordion-atlas";
import { RdsModal } from "@rds-vue-ui/rds-modal";
import ButtonApplyNow from "~/components/nav/ButtonApplyNow.vue";
import type { ExploreItem } from "~/components/students/SectionExploreYourFuture.vue";
import SectionExploreYourFuture from "~/components/students/SectionExploreYourFuture.vue";
import SectionServicesGrid from "~/components/students/SectionServicesGrid.vue";
import SectionServicesSupportGrid from "~/components/students/SectionServicesSupportGrid.vue";
import type {
  ParagraphHtmlLink,
  ParagraphTestimonialApollo,
  StudentsServices
} from "~/server/resolvers/resolvers-types";
import type { StudentsServicesQuery } from "~/types/graphql-operations";

const getDefaultAccordionImage = ref("");

definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"]
  }
});

const navItems = {
  "services-support-team": "Support Team",
  "services-student-services": "Student Services",
  "services-career-services": "Career Services",
  "services-community": "Community",
  "services-faq": "FAQ"
};

const applyNowUrl = "#";
const applyNowType = "modal";
const modalContent = ref("");
const modalAuthor = ref("");

const quoteModal = ref<HTMLElement | null>();
const displayModal = ref(false);

const UtilityService = useUtilityService();
const config = useRuntimeConfig();

const $page = await usePageQuery<StudentsServicesQuery>(
  /* GraphQL */ `
    #graphql
    query studentsServices($studentsServicesId: ID!) {
      studentsServices(id: $studentsServicesId) {
        detailPage
        metaTitle
        metaDescription
        heroImage
        title
        introTitle
        introDescription
        introImage
        introImageAlt
        introBackground
        introLink {
          text
          uri
          target
        }
        accordionTitle
        accordionItems {
          id
          accordionId
          title
          body
          image
        }
        careerTitle
        careerSubtitle
        careerDescription
        careerLink {
          text
          uri
          target
        }
        careerListItem {
          title
          icon
          iconAlt
        }
        communitiesTitle
        communitiesSubtitle
        communitiesImage
        comminitiesImageAlt
        communitiesDescription
        communitiesLink {
          text
          uri
          target
        }
        supportTitle
        supportSubtitle
        supportImage
        supportImageAlt
        supportDescription
        supportLink {
          text
          uri
          target
        }
        testimonialTitle
        testimonialLabel
        testimonialBackground
        testimonialCards {
          quote
          authorName
          authorDesignation
          authorImage
        }
        faqTitle
        faqItems {
          title
          description
        }
        exploreTitle
        exploreBg
        exploreCards {
          title
          image
          imageAlt
          link {
            text
            uri
            target
          }
        }
      }
    }
  `,
  {
    studentsServicesId: "bd59afca-4981-4dbd-a593-69b23df2da75"
  },
  {
    key: "student-services"
  }
);

useHead({
  title: $page?.studentsServices?.metaTitle,
  meta: [
    { name: "Title", content: $page?.studentsServices?.metaTitle },
    {
      name: "Description",
      content: $page?.studentsServices?.metaDescription
    }
  ],
  link: [
    {
      rel: "canonical",
      href: UtilityService.formatCanonicalUrl(
        `${config.public.siteUrl}${$page?.studentsServices?.detailPage}`
      )
    }
  ],
  script: [
    useSchemaService().generateStudentsServicesSchema(
      $page?.studentsServices as unknown as StudentsServices
    )
  ]
});

const getIntroBgPattern = computed(() => {
  return {
    "--intro-bg": `url('${$page?.studentsServices?.introBackground}')`
  };
});

const handleFaqCollapseClick = (action: string, section: string, text: string) => {
  analyticsComposable.trackCollapseEvent(
    "onclick",
    action.toLowerCase(),
    "click",
    "main content",
    section.toLowerCase(),
    text.toLowerCase()
  );
};

const scrollToSection = (target: string) => {
  const targetElement = document.getElementById(target);
  const targetElemTop = targetElement?.getBoundingClientRect().top;
  let navbarOffset = 59;
  if (window.innerWidth < 992) {
    navbarOffset = 65;
  }
  const displacement = targetElemTop! - navbarOffset;
  const userAgent = window.navigator.userAgent;
  // Support for IE and Edge
  if (userAgent.match(/Trident\/[6,7]\./) || userAgent.match(/Edge/)) {
    window.scrollBy(0, displacement);
  } else {
    window.scrollBy({
      top: displacement,
      behavior: "smooth"
    });
  }
};

const scrollToRfi = (section: string, region: string, component: string, target: string) => {
  analyticsComposable.trackEvent({
    event: "link",
    name: "onclick",
    action: "click",
    type: "internal link",
    region: region.toLowerCase(),
    section: section.toLowerCase(),
    text: "request info",
    component: component.toLowerCase()
  });
  scrollToSection(target.toLowerCase());
};

const handleSuccessTeamLinkClick = (link: ParagraphHtmlLink, section: string) => {
  if (link && link.text && link.uri && link.target) {
    analyticsComposable.trackLinkEvent(
      "internal link",
      "main content",
      section.toLowerCase(),
      link.text.toLowerCase()
    );
    navigateTo(link.uri, {
      open: {
        target: link.target
      }
    });
  } else {
    console.error("link is missing target, text, or href");
  }
};

const handleCollapseEvent = (action: string, section: string, text: string) => {
  analyticsComposable.trackCollapseEvent(
    "onclick",
    action.toLowerCase(),
    "click",
    "main content",
    section.toLowerCase(),
    text.toLowerCase(),
    "accordion-standard"
  );
};

const showQuoteModal = (content: ParagraphTestimonialApollo) => {
  // show modal
  if (content.quote && content.authorName) {
    displayModal.value = true;
    modalContent.value = content.quote;
    modalAuthor.value = content.authorName;
    // analytics event
    analyticsComposable.trackModalEvent(
      "onclick",
      "open",
      "click",
      "main content",
      `${$page?.studentsServices?.testimonialTitle!.toLowerCase()} ^ ${content.authorName.toLowerCase()}`,
      "see full quote"
    );
  } else {
    console.error("missing quote or authorName for the modal");
  }
};

const handleModalClose = () => {
  analyticsComposable.trackModalEvent(
    "onclick",
    "close",
    "click",
    "main content",
    `${$page?.studentsServices?.testimonialTitle!.toLowerCase()} ^ ${modalAuthor.value.toLowerCase()}`,
    "close cross"
  );
  displayModal.value = false;
};

const handleCarouselClick = (title: string, type: string, text: string) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    type,
    "main content",
    title.toLowerCase(),
    text
  );
};

watchEffect(() => {
  const accordionItems = $page?.studentsServices?.accordionItems;
  if (accordionItems && accordionItems.length > 0) {
    getDefaultAccordionImage.value = accordionItems[0]?.image ?? "";
  }
});
</script>

<style lang="scss">
/* List CSS */
.service-accordion-section .rds-list {
  columns: 2;
  padding-left: 1rem;
}

/* Smaller devices */
@media (max-width: 992px) {
  .service-accordion-section .rds-list li {
    columns: 1;
  }
}
</style>

<style lang="scss" scoped>
.carousel-img {
  height: 212px;
  border-radius: 50%;
}

:deep(.intro-section .container) {
  background-image: var(--intro-bg) !important;
  max-width: 960px !important;
}
.career-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;
}
.icon-list {
  margin-bottom: 60px;

  &:nth-last-of-type(-n + 2),
  &:last-of-type {
    margin-bottom: 0;
  }
}
.career-icon {
  width: 64px !important;
  height: 64px !important;
  border-radius: 50%;
  margin-right: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;

  img {
    max-width: 40px;
    max-height: 40px;
  }
}
.community-img {
  min-height: 420px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 10;
}

.support-img {
  background-position: center;
  background-repeat: no-repeat;
  z-index: 10;
}
.separator-underline {
  height: 8px;
  width: 64px;
}
.accordion-highlight {
  width: 54px;
  height: 6px;
}
.faq-highlight {
  width: 64px;
  height: 8px;
}
.accordion-gap {
  margin-bottom: 1rem;

  &:last-of-type {
    margin-bottom: 0;
  }
}

@media screen and (min-width: 992px) {
  :deep(.intro-section .soa-image) {
    width: 100%;
    object-fit: cover;
  }
  :deep(.intro-section .bg-image-div) {
    max-width: 500px;
  }
  .icon-list {
    display: flex;
    align-items: center;
  }
  .career-icon {
    margin-bottom: 0;
    flex-shrink: 0;
  }
  .accordion-gap {
    margin-bottom: 1.5rem;
  }
  .community-img {
    min-height: 500px;
  }
}
/* Large Devices */
@media screen and (min-width: 1200px) {
  .community-img {
    min-height: 650px;
  }
}

@media (max-width: 767px) {
  .support-img {
    height: 300px;
    min-height: 300px;
    background-size: contain;
  }
}

@media (min-width: 768px) and (max-width: 1024px) {
  .support-img {
    height: 426px;
    min-height: 400px;
    background-size: cover;
  }
}

.supportTeam:focus-visible {
  outline: 2px solid white;
}

:deep(.indicator-dots) {
  width: 16px;
  height: 16px;
  padding: 4px;
  box-sizing: content-box;
}
</style>
